/*
 * 顶部付费入口 Hover 卡（webVipEntrance AB 实验组）· 替代旧版 VipHoverPopover
 *
 * 状态机：身份（6 种）× 存储阶段（4 种）× 临期/补差价
 * 数据流：通过 useEffective* hooks 读真实/dev mock，与 VipEntranceBanner 共用同一套判定
 *
 * Phase B 暂留：4 套权益图标先共用「通用权益」3 个，等设计稿补齐其余 3 套再切
 */
import React, { useMemo } from 'react'
import dayjs from 'dayjs'
import {
  useVip,
  useVipKind,
  useEffectiveStorage,
  useEffectiveExpireDays,
  useEffectiveCanDiffUpgrade
} from '../../../app/hooks'
import CONFIG from '../../../constants/config'
import {
  storageEntranceCustomContent,
  StorageStage as VariantStage
} from '../storageEntranceVariant'

import photoIcon from './images/hover_card_photo.svg?url'
import memberIcon from './images/hover_card_member.svg?url'
// 权益图标内联为 React 组件（?react），fill 走 currentColor 以便跟随等级配色
import BenefitStorageIcon from './images/hover_card_benefit_storage.svg?react'
import BenefitViewIcon from './images/hover_card_benefit_view.svg?react'
import BenefitDownloadIcon from './images/hover_card_benefit_download.svg?react'

import './VipEntranceHoverCard.scss'

interface Props {
  /** 主按钮点击 · Header 透传 newOnPay(false) 等智能路由
   *  · 存储告警态下由卡片内部计算 customContent 透传给开通弹窗 */
  onPay: (customContent?: { variant: string; data: any } | null) => void
  /** 大客户定制会员点击 · Header 透传 */
  onPressKeyAccount: () => void
  /** 查看全部权益点击 · 跳 /privileges 页（透传 Header 已实现的链路） */
  onViewAllBenefits: () => void
  /** 到期天数（真实值，会被 useEffectiveExpireDays 接力 mock） */
  expireDays: number
  /** 到期时间戳（unix 秒，真实值）· 用于徽标旁的「YYYY/MM/DD 到期」；mock 下按 expireDays 推算 */
  expireTime?: number
  /** 真实的补差价升级资格（由调用方 fetchUpdateGoodsInfo 拿到，mock 时可覆盖） */
  realCanDiffUpgrade?: boolean
}

type Identity = 'guest' | 'vip' | 'vvip' | 'svip' | 'expiring' | 'expired'
type StorageStage = 'normal' | 'near' | 'over_safe' | 'over_limited'

interface BenefitItem {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
  label: string
}

const EXPIRING_DAYS_MAX = 30
const STORAGE_NEAR_RATIO = 0.9

// 通用 3 件权益（非会员 / 过期）· 其余 3 套权益设计稿到位后替换
const BENEFITS_COMMON: BenefitItem[] = [
  { icon: BenefitStorageIcon, label: '存储扩容' },
  { icon: BenefitViewIcon, label: '无限查看' },
  { icon: BenefitDownloadIcon, label: '批量下载' }
]

// 存储权益文案 · 按会员等级展示对应存储额度（临期/过期按其原等级），非会员走通用「存储扩容」
const storageLabelForKind = (vipKind: number): string | null => {
  if (vipKind === CONFIG.VIP_KIND.SVIP) return '50 万张存储'
  if (vipKind === CONFIG.VIP_KIND.VVIP) return '20 万张存储'
  if (vipKind === CONFIG.VIP_KIND.VIP) return '10 万张存储'
  return null
}

// 等级标签 · 优先级 SVIP > VVIP > VIP > 免费版
const labelForKind = (vipKind: number): string => {
  if (vipKind === CONFIG.VIP_KIND.SVIP) return '超级会员'
  if (vipKind === CONFIG.VIP_KIND.VVIP) return '高级会员'
  if (vipKind === CONFIG.VIP_KIND.VIP) return '基础会员'
  return '免费版'
}

// 格式化照片张数 · -1 为后端「无限存储」哨兵值 → 显示「无限」；≥ 1 万显示「X 万张」，否则千分位
const formatPhotoNum = (n: number): string => {
  if (n < 0) return '无限'
  if (n >= 10000) {
    const w = n / 10000
    return `${Number.isInteger(w) ? w : w.toFixed(1)} 万张`
  }
  return n.toLocaleString('en-US') + ' 张'
}

// 按 PRD 1.3 #5: 权益区标题
const BENEFITS_TITLE_MAP: Record<Identity, string> = {
  guest: '开通解锁以下权益',
  vip: '升级解锁以下权益',
  vvip: '升级解锁以下权益',
  svip: '已解锁权益',
  expiring: '续费继续享受以下权益',
  expired: '续费即可恢复以下权益'
}

// 按 PRD 1.2 表: 按钮文案
const BTN_TEXT_MAP: Record<Identity, string> = {
  guest: '开通会员',
  vip: '升级会员',
  vvip: '升级会员',
  svip: '会员中心',
  expiring: '续费会员',
  expired: '续费会员'
}

const VipEntranceHoverCard: React.FC<Props> = ({
  onPay,
  onPressKeyAccount,
  onViewAllBenefits,
  expireDays: realExpireDays,
  expireTime,
  realCanDiffUpgrade = false
}) => {
  const { isVip, isSVip, isVVip } = useVip()
  const vipKind = useVipKind()
  const expireDays = useEffectiveExpireDays(realExpireDays)
  const canDiffUpgrade = useEffectiveCanDiffUpgrade(realCanDiffUpgrade)
  const { totalNum, limitPhotoNum, memberCount, memberLimit, viewLimited } =
    useEffectiveStorage()

  const storageRatio = limitPhotoNum > 0 ? totalNum / limitPhotoNum : 0
  const memberRatio = memberLimit > 0 ? memberCount / memberLimit : 0

  // 存储进度条颜色 / 成员进度条颜色
  const storageBarTone =
    storageRatio >= 1
      ? 'over'
      : storageRatio >= STORAGE_NEAR_RATIO
        ? 'near'
        : 'normal'
  const memberBarTone = memberRatio >= 1 ? 'over' : 'normal'

  // 身份判定 · 优先级：过期 > 临期 > SVIP > VVIP > VIP > 非会员
  const identity: Identity = useMemo(() => {
    if (!isVip) return 'guest'
    if (expireDays < 0) return 'expired'
    if (expireDays <= EXPIRING_DAYS_MAX) return 'expiring'
    if (isSVip) return 'svip'
    if (isVVip) return 'vvip'
    return 'vip'
  }, [isVip, isSVip, isVVip, expireDays])

  // 存储阶段 · 会员（在期 VIP/VVIP/SVIP）查看不受限，over_limited 永远走 over_safe
  const storageStage: StorageStage = useMemo(() => {
    if (storageRatio < STORAGE_NEAR_RATIO) return 'normal'
    if (storageRatio < 1) return 'near'
    const isPaidMember =
      identity === 'vip' || identity === 'vvip' || identity === 'svip'
    if (!isPaidMember && viewLimited) return 'over_limited'
    return 'over_safe'
  }, [storageRatio, viewLimited, identity])

  // 等级标签
  const levelLabel = identity === 'guest' ? '免费版' : labelForKind(vipKind)

  // 等级标签配色修饰类 · 跟会员等级走（临期/过期仍按其原等级配色），非会员用免费版灰
  const levelClass = (() => {
    if (identity === 'guest') return 'free'
    if (vipKind === CONFIG.VIP_KIND.SVIP) return 'svip'
    if (vipKind === CONFIG.VIP_KIND.VVIP) return 'vvip'
    if (vipKind === CONFIG.VIP_KIND.VIP) return 'vip'
    return 'free'
  })()

  // 徽标旁到期日期 · 免费版无到期不展示；用 expireTime 时间戳（unix 秒）格式化
  const expireDate = useMemo(() => {
    if (identity === 'guest') return ''
    return expireTime ? dayjs.unix(expireTime).format('YYYY/MM/DD') : ''
  }, [identity, expireTime])

  // 标题计算
  const title = useMemo(() => {
    // 临期：倒数态，存储不细分
    if (identity === 'expiring') {
      return expireDays === 0 ? '会员今日到期' : `会员 ${expireDays} 天后到期`
    }

    // 存储告警优先级高于默认营销态
    if (storageStage === 'over_limited') {
      // 仅非会员/过期会触发
      return identity === 'expired'
        ? '存储已满，续费会员恢复查看'
        : '存储已满，开通会员恢复查看'
    }
    if (storageStage === 'over_safe') {
      return '存储已满，超限照片将无法查看'
    }
    if (storageStage === 'near') {
      const remain = Math.max(1, Math.ceil((1 - storageRatio) * 100))
      return `存储仅剩 ${remain}%，超限照片将无法查看`
    }

    // 默认态按身份分支
    switch (identity) {
      case 'guest':
        return '开通会员解锁全部权益'
      case 'expired':
        return '会员已过期，续费即恢复权益'
      case 'svip':
        return '已解锁全部权益'
      case 'vip':
      case 'vvip':
        return canDiffUpgrade
          ? '128 元起补差价升级，解锁更多权益'
          : '升级会员解锁更多权益'
      default:
        return ''
    }
  }, [identity, storageStage, storageRatio, expireDays, canDiffUpgrade])

  const benefitsTitle = BENEFITS_TITLE_MAP[identity]
  const btnText = BTN_TEXT_MAP[identity]
  // Phase B：4 套权益图标暂统一用通用 3 件，等设计稿补齐再扩
  // 存储项文案按等级差异化（临期/过期跟原等级走），非会员保持「存储扩容」
  const benefits = useMemo(() => {
    const storageLabel =
      identity === 'guest' ? null : storageLabelForKind(vipKind)
    if (!storageLabel) return BENEFITS_COMMON
    return BENEFITS_COMMON.map((b, i) =>
      i === 0 ? { ...b, label: storageLabel } : b
    )
  }, [identity, vipKind])

  // PRD 03：仅存储告警态（near / over_safe / over_limited）替换弹窗顶部标题/副标题
  const handlePayClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (storageStage === 'normal') {
      onPay()
      return
    }
    const stage = storageStage as VariantStage
    const remainPercent =
      stage === 'near'
        ? Math.max(1, Math.ceil((1 - storageRatio) * 100))
        : undefined
    onPay(
      storageEntranceCustomContent({
        stage,
        isMember: !!isVip,
        totalNum,
        limitPhotoNum,
        remainPercent,
        levelName: identity === 'guest' ? '' : labelForKind(vipKind)
      })
    )
  }
  const handleViewAllClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onViewAllBenefits()
  }
  const handleKeyAccountClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onPressKeyAccount()
  }

  return (
    <div className="vip-entrance-hover-card" role="dialog">
      {/* Section 1 · Header */}
      <header className="vip-entrance-hover-card__header">
        <div className="vip-entrance-hover-card__level-row">
          <span
            className={`vip-entrance-hover-card__level vip-entrance-hover-card__level--${levelClass}`}
          >
            {levelLabel}
          </span>
          {expireDate && (
            <span className="vip-entrance-hover-card__expire">
              {expireDate} 到期
            </span>
          )}
        </div>
        <h3 className="vip-entrance-hover-card__title">{title}</h3>
      </header>

      {/* Section 2 · Stats */}
      <section className="vip-entrance-hover-card__stats">
        <div className="vip-entrance-hover-card__stat-row">
          <div className="vip-entrance-hover-card__stat-line">
            <span className="vip-entrance-hover-card__stat-label">
              <img src={photoIcon} alt="" />
              照片
            </span>
            <span className="vip-entrance-hover-card__stat-value">
              <strong>{totalNum.toLocaleString('en-US')}</strong>
              {/* -1 为无限存储：只显示当前张数，不展示「/ 上限」 */}
              {limitPhotoNum < 0 ? (
                <span> 张</span>
              ) : (
                <span> / {formatPhotoNum(limitPhotoNum)}</span>
              )}
              {/* 临期会员固定提示：到期后存储降至免费版 1 万张 */}
              {identity === 'expiring' && (
                <span style={{ color: '#d34537', marginLeft: 4 }}>
                  (到期后将降至 1 万张)
                </span>
              )}
            </span>
          </div>
          <div
            className={`vip-entrance-hover-card__bar vip-entrance-hover-card__bar--${storageBarTone}`}
          >
            <div
              className="vip-entrance-hover-card__bar-fill"
              style={{ width: `${Math.min(100, storageRatio * 100)}%` }}
            />
          </div>
        </div>

        <div className="vip-entrance-hover-card__stat-row">
          <div className="vip-entrance-hover-card__stat-line">
            <span className="vip-entrance-hover-card__stat-label">
              <img src={memberIcon} alt="" />
              团队成员
            </span>
            <span className="vip-entrance-hover-card__stat-value">
              <strong>{memberCount}</strong>
              <span> / {memberLimit} 人</span>
            </span>
          </div>
          <div
            className={`vip-entrance-hover-card__bar vip-entrance-hover-card__bar--${memberBarTone}`}
          >
            <div
              className="vip-entrance-hover-card__bar-fill"
              style={{ width: `${Math.min(100, memberRatio * 100)}%` }}
            />
          </div>
        </div>
      </section>

      {/* Section 3 · Benefits */}
      <section className="vip-entrance-hover-card__benefits">
        <div className="vip-entrance-hover-card__benefits-head">
          <span
            className={`vip-entrance-hover-card__benefits-title vip-entrance-hover-card__benefits-title--${levelClass}`}
          >
            {benefitsTitle}
          </span>
          <button
            type="button"
            className={`vip-entrance-hover-card__view-all vip-entrance-hover-card__view-all--${levelClass}`}
            onClick={handleViewAllClick}
          >
            查看全部 ›
          </button>
        </div>
        <div className="vip-entrance-hover-card__benefits-grid">
          {benefits.map((b) => (
            <div
              key={b.label}
              className={`vip-entrance-hover-card__benefit-card vip-entrance-hover-card__benefit-card--${levelClass}`}
            >
              {/* 内联 SVG，fill 由 CSS 的 currentColor 接管以跟随等级配色 */}
              <b.icon
                className="vip-entrance-hover-card__benefit-icon"
                aria-hidden
              />
              <span>{b.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Section 4 · Footer Buttons */}
      <footer className="vip-entrance-hover-card__footer">
        <button
          type="button"
          className={`vip-entrance-hover-card__btn-primary vip-entrance-hover-card__btn-primary--${levelClass}`}
          onClick={handlePayClick}
        >
          {btnText}
        </button>
        <button
          type="button"
          className="vip-entrance-hover-card__btn-secondary"
          onClick={handleKeyAccountClick}
        >
          大客户定制会员
        </button>
      </footer>
    </div>
  )
}

export default VipEntranceHoverCard
